import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd } from "@/components/Seo";
import PropertyCard from "@/components/PropertyCard";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Building2, CalendarClock, ReceiptText, Headphones, CheckCircle2, ArrowRight } from "lucide-react";

const PERKS = [
  { icon: CalendarClock, title: "Séjours longue durée", d: "Tarifs dégressifs pour vos déplacements professionnels et missions." },
  { icon: ReceiptText, title: "Facturation société", d: "Facture au nom de votre entreprise, TVA et justificatifs fournis." },
  { icon: Headphones, title: "Interlocuteur dédié", d: "Un contact unique pour toutes vos réservations d'équipe." },
  { icon: Building2, title: "Logements équipés", d: "Wifi, espace de travail et équipements adaptés aux pros." },
];

export default function BusinessRental() {
  const [props, setProps] = useState([]);
  const [form, setForm] = useState({ company: "", name: "", email: "", phone: "", headcount: "", duration: "", message: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/properties?audience=entreprises").then((r) => setProps(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/entreprise-contact", form);
      setDone(true);
      toast.success("Demande envoyée ! Nous vous recontactons rapidement.");
    } catch (err) { toast.error(apiErr(err)); } finally { setLoading(false); }
  };

  return (
    <Layout>
      <Seo title="Location entreprises à Rouen | Logements professionnels | NJ Conciergerie"
        description="Location de logements pour entreprises à Rouen : déplacements professionnels, séjours longue durée, facturation société. Appartements équipés par NJ Conciergerie."
        canonical="/location-entreprises" jsonLd={useLocalBusinessLd()} />

      {/* HERO */}
      <section className="bg-nj-primary text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Entreprises · Rouen</span>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
              Des logements pour vos équipes en déplacement
            </h1>
            <p className="text-white/80 text-lg mt-5 max-w-xl leading-relaxed">
              Missions, mutations, chantiers ou séminaires : NJ Conciergerie loge vos collaborateurs à Rouen dans des appartements équipés, avec facturation société.
            </p>
            <a href="#entreprise-form" className="inline-flex items-center gap-2 mt-8 bg-nj-gold text-nj-dark font-semibold px-6 py-3 rounded-sm hover:brightness-105 transition-all">
              Demander un devis <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="relative hidden lg:block">
            <img src="https://images.unsplash.com/photo-1613575831056-0acd5da8f085?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Appartement professionnel équipé à Rouen avec espace de travail"
              className="rounded-md w-full h-[420px] object-cover shadow-2xl" />
            <div className="absolute -bottom-4 -left-4 bg-nj-gold text-nj-dark px-5 py-3 rounded-sm font-semibold shadow-lg">
              Facturation société incluse
            </div>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {PERKS.map(({ icon: Icon, title, d }) => (
            <div key={title} className="flex flex-col gap-3" data-testid={`perk-${title}`}>
              <div className="w-12 h-12 rounded-sm bg-nj-olive flex items-center justify-center"><Icon className="w-6 h-6 text-nj-primary" /></div>
              <h3 className="font-serif text-xl text-nj-text">{title}</h3>
              <p className="text-nj-text2 text-sm leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROPERTIES */}
      {props.length > 0 && (
        <section className="bg-nj-olive/30 border-y border-nj-olive">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
            <div className="flex items-end justify-between gap-4 mb-10">
              <div>
                <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Sélection entreprises</span>
                <h2 className="font-serif font-light text-3xl sm:text-4xl text-nj-text mt-3">Logements adaptés aux professionnels</h2>
              </div>
              <Link to="/logements?public=entreprises" className="hidden md:inline-flex items-center gap-2 text-nj-primary font-medium hover:gap-3 transition-all">
                Tout voir <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="business-property-list">
              {props.slice(0, 6).map((p) => <PropertyCard key={p.id} p={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* FORM */}
      <section id="entreprise-form" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {done ? (
          <div className="text-center py-16" data-testid="entreprise-done">
            <CheckCircle2 className="w-16 h-16 text-nj-primary mx-auto" />
            <h2 className="font-serif font-light text-3xl text-nj-text mt-6">Merci !</h2>
            <p className="text-nj-text2 mt-3">Votre demande a bien été transmise. Un conseiller NJ Conciergerie vous recontacte rapidement.</p>
          </div>
        ) : (
          <>
            <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">Votre demande</span>
            <h2 className="font-serif font-light text-3xl sm:text-4xl text-nj-text mt-3">Parlons de vos besoins</h2>
            <p className="text-nj-text2 mt-3">Décrivez votre besoin, nous vous proposons une solution et un devis adaptés.</p>
            <form onSubmit={submit} className="mt-8 grid gap-5 sm:grid-cols-2" data-testid="entreprise-form-el">
              <div><Label>Société *</Label><Input required data-testid="ent-company" value={form.company} onChange={set("company")} className="mt-1.5" /></div>
              <div><Label>Nom du contact *</Label><Input required data-testid="ent-name" value={form.name} onChange={set("name")} className="mt-1.5" /></div>
              <div><Label>Email *</Label><Input required type="email" data-testid="ent-email" value={form.email} onChange={set("email")} className="mt-1.5" /></div>
              <div><Label>Téléphone</Label><Input data-testid="ent-phone" value={form.phone} onChange={set("phone")} className="mt-1.5" /></div>
              <div><Label>Nombre de voyageurs</Label><Input data-testid="ent-headcount" value={form.headcount} onChange={set("headcount")} className="mt-1.5" placeholder="ex : 3 collaborateurs" /></div>
              <div><Label>Durée souhaitée</Label><Input data-testid="ent-duration" value={form.duration} onChange={set("duration")} className="mt-1.5" placeholder="ex : 2 semaines, 3 mois…" /></div>
              <div className="sm:col-span-2"><Label>Votre besoin *</Label><Textarea required rows={4} data-testid="ent-message" value={form.message} onChange={set("message")} className="mt-1.5" placeholder="Dates, quartier souhaité, budget, facturation…" /></div>
              <div className="sm:col-span-2">
                <Button type="submit" disabled={loading} data-testid="ent-submit" className="bg-nj-primary hover:bg-nj-primaryHover text-white rounded-sm px-8 py-6 text-base">
                  {loading ? "Envoi…" : "Envoyer ma demande"}
                </Button>
              </div>
            </form>
          </>
        )}
      </section>
    </Layout>
  );
}
